/**
 * A read-only view of an action set, grouped by feature. Ported from
 * `bimgo-ai-web`'s `PermissionsDialog`, adapted to the local `Modal` and `t`.
 *
 * The look is the web client's: one card per feature, headed by a tinted icon
 * tile, with each action as a check-marked pill in that feature's colour.
 * Tailwind's palette is inlined here (emerald / blue / amber / gray) because 2.5
 * has no utility CSS.
 */
import { useMemo } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "./Modal";

const FEATURE_ORDER = ["org", "project", "skill", "chat"];

/** bg / fg / border per feature, matching bimgo-ai-web's chip classes. */
const FEATURE_COLORS: Record<string, { bg: string; fg: string; border: string; tileBg: string; tileFg: string }> = {
  org: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0", tileBg: "#ecfdf5", tileFg: "#059669" },
  project: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe", tileBg: "#eff6ff", tileFg: "#2563eb" },
  skill: { bg: "#fffbeb", fg: "#b45309", border: "#fde68a", tileBg: "#fffbeb", tileFg: "#d97706" },
  chat: { bg: "#f9fafb", fg: "#374151", border: "#e5e7eb", tileBg: "#f3f4f6", tileFg: "#4b5563" },
};
const FALLBACK_COLORS = FEATURE_COLORS["chat"] as NonNullable<(typeof FEATURE_COLORS)[string]>;

function FeatureIcon({ feature }: { feature: string }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (feature) {
    case "org": // Building2
      return (
        <svg {...common}>
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
        </svg>
      );
    case "project": // FolderKanban
      return (
        <svg {...common}>
          <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
          <path d="M8 10v6M12 10v2M16 10v4" />
        </svg>
      );
    case "skill": // Wand2
      return (
        <svg {...common}>
          <path d="m3 21 9-9" />
          <path d="M15 4V2M15 10V8M12.5 5.5h-2M19.5 5.5h-2M17 3.5 15.6 4.9M17 7.5l-1.4-1.4M13 3.5l1.4 1.4M13 7.5l1.4-1.4" />
        </svg>
      );
    case "chat": // MessageSquare
      return (
        <svg {...common}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    default: // KeyRound
      return (
        <svg {...common}>
          <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
          <circle cx="16.5" cy="7.5" r=".5" />
        </svg>
      );
  }
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function groupActions(actions: string[]): [string, string[]][] {
  const groups: Record<string, string[]> = {};
  for (const action of actions) {
    const sep = action.indexOf(":");
    const feature = sep > 0 ? action.slice(0, sep) : action;
    const sub = sep > 0 ? action.slice(sep + 1) : action;
    const existing = groups[feature] ?? [];
    existing.push(sub);
    groups[feature] = existing;
  }
  const known = FEATURE_ORDER.filter((f) => f in groups).map(
    (f) => [f, (groups[f] ?? []).sort()] as [string, string[]],
  );
  const unknown = Object.entries(groups)
    .filter(([f]) => !FEATURE_ORDER.includes(f))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([f, subs]) => [f, subs.sort()] as [string, string[]]);
  return [...known, ...unknown];
}

export function PermissionsDialog({
  open,
  onClose,
  title,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  actions: string[];
}) {
  const { t } = useLocale();
  const grouped = useMemo(() => groupActions(actions), [actions]);

  const featureLabel = (feature: string) => {
    const key = `settings.user.actions.feature.${feature}`;
    const label = t(key);
    return label === key ? feature : label;
  };
  const actionLabel = (feature: string, action: string) => {
    const key = `settings.user.actions.action.${feature}.${action}`;
    const label = t(key);
    return label === key ? action : label;
  };

  return (
    <Modal open={open} onClose={onClose} title={title} wide>
      {grouped.length === 0 ? (
        <p className="settings-note" style={{ textAlign: "center", padding: "1rem 0" }}>
          {t("settings.user.actions.none")}
        </p>
      ) : (
        <div className="perm-groups">
          {grouped.map(([feature, subs]) => {
            const c = FEATURE_COLORS[feature] ?? FALLBACK_COLORS;
            return (
              <div key={feature} className="perm-group">
                <div className="perm-group-head">
                  <span
                    className="perm-tile"
                    style={{ background: c.tileBg, color: c.tileFg }}
                  >
                    <FeatureIcon feature={feature} />
                  </span>
                  <h4 className="perm-feature">{featureLabel(feature)}</h4>
                </div>
                <div className="perm-chips">
                  {subs.map((sub) => (
                    <span
                      key={sub}
                      className="perm-chip"
                      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                    >
                      <CheckIcon />
                      {actionLabel(feature, sub)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
